import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type {
  AwardCriterionJson,
  OpportunityConstraints,
} from '../../types/dce-analysis.types';

interface Props {
  constraints?: OpportunityConstraints;
}

const TYPE_LABELS: Record<string, string> = {
  prix: 'Prix',
  technique: 'Technique',
  delai: 'Délai',
  methode: 'Méthode',
  autre: 'Autre',
};

const TYPE_COLORS: Record<string, string> = {
  prix: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  technique: 'bg-blue-100 text-blue-700 border-blue-300',
  delai: 'bg-amber-100 text-amber-700 border-amber-300',
  methode: 'bg-purple-100 text-purple-700 border-purple-300',
  autre: 'bg-gray-100 text-gray-700 border-gray-300',
};

export function CriteriaSection({ constraints }: Props) {
  const criteria = parseCriteria(constraints?.awardCriteria);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Critères d'attribution {criteria.length > 0 && `(${criteria.length})`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {criteria.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Aucun critère d'attribution extrait pour cette opportunité.
          </p>
        ) : (
          <div className="space-y-3">
            {criteria.map((c, i) => (
              <CriterionRow key={i} criterion={c} />
            ))}
            <CriteriaTotal criteria={criteria} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CriterionRow({ criterion }: { criterion: AwardCriterionJson }) {
  const widthPct = Math.max(2, Math.min(100, criterion.weightPercent));
  const typeColor = TYPE_COLORS[criterion.type] ?? TYPE_COLORS.autre;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{criterion.name}</span>
          <Badge variant="outline" className={typeColor}>
            {TYPE_LABELS[criterion.type] ?? criterion.type}
          </Badge>
        </div>
        <span className="font-semibold tabular-nums">{criterion.weightPercent}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-copper transition-all"
          style={{ width: `${widthPct}%` }}
        />
      </div>
      {criterion.subCriteria.length > 0 && (
        <ul className="mt-1.5 ml-3 text-xs text-muted-foreground">
          {criterion.subCriteria.map((sc, j) => (
            <li key={j}>• {sc}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CriteriaTotal({ criteria }: { criteria: AwardCriterionJson[] }) {
  const total = criteria.reduce((s, c) => s + c.weightPercent, 0);
  const isValid = total === 100;
  return (
    <div className="border-t pt-2 mt-2 text-xs">
      <span className="text-muted-foreground">Somme des pondérations : </span>
      <span className={`font-semibold tabular-nums ${isValid ? 'text-emerald-600' : 'text-amber-600'}`}>
        {total}%
      </span>
      {!isValid && (
        <span className="ml-2 text-amber-600 italic">
          (devrait être 100 % — possible incohérence du DCE)
        </span>
      )}
    </div>
  );
}

function parseCriteria(awardCriteria: unknown): AwardCriterionJson[] {
  if (!Array.isArray(awardCriteria)) return [];
  return awardCriteria.filter(
    (c): c is AwardCriterionJson =>
      !!c && typeof c === 'object' && 'name' in c && 'weightPercent' in c,
  );
}
